import { recipe } from "@vanilla-extract/recipes";
import { rem } from "@huub-web/utils/css/rem";
import { style } from "@vanilla-extract/css";

import { theme } from "../../theme/css/theme.css";
import { responsive } from "../../utils/responsive";
import { transition } from "../../utils/transition";
import { alignVariants } from "../../utils/vanilla/alignVariants";

export const avatarStyle = style({
  // Keep
});

export const avatarContainerStyle = recipe({
  base: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    position: "relative",
    flexShrink: 0,
    borderRadius: "50%",
    border: "none",
    zIndex: 1,
    selectors: {
      "& + &": {
        marginLeft: `calc(${theme.spacings.xs} * -1)`,
        boxSizing: "content-box",
        outline: `1px solid var(--background, ${theme.colors.background})`,
      },
      "&:before": {
        content: "''",
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        background: "var(--avatar-background, transparent)",
        zIndex: 1,
      },
      // "& + &:before": {
      //   outline: `1px solid var(--avatar-background, ${theme.colors.background})`,
      // },
    },
    // ":after": {
    //   content: "''",
    //   position: "absolute",
    //   top: 0,
    //   left: 0,
    //   width: "100%",
    //   height: "100%",
    //   borderRadius: "50%",
    //   background: `var(--background, ${theme.colors.background})`,
    //   zIndex: 0,
    //   pointerEvents: "none",
    // },
  },
  variants: {
    alignSelf: alignVariants("alignSelf"),
    justifySelf: alignVariants("justifySelf"),
    size: {
      xxs: {
        width: rem(20),
        height: rem(20),
      },
      xs: {
        width: rem(28),
        height: rem(28),
      },
      s: {
        width: rem(32),
        height: rem(32),
      },
      m: {
        width: rem(36),
        height: rem(36),
      },
      l: {
        width: rem(48),
        height: rem(48),
      },
      menu: {
        ...responsive({
          base: {
            width: rem(36),
            height: rem(36),
          },
          s: {
            width: rem(42),
            height: rem(42),
          },
        }),
      },
      xl: {
        width: rem(64),
        height: rem(64),
      },
      xxl: {
        width: rem(92),
        height: rem(92),
      },
      xxxl: {
        width: rem(120),
        height: rem(120),
      },
    },
    isUser: {
      true: {
        vars: {
          "--background": theme.colors.background,
        },
      },
    },
    variant: {
      primary: {
        vars: {
          "--avatar-background": theme.colors.primary,
        },
        color: theme.colors.onPrimary,
      },
      secondary: {
        vars: {
          "--avatar-background": theme.colors.secondary,
        },
        color: theme.colors.onSecondary,
      },
      tertiary: {
        vars: {
          "--avatar-background": theme.colors.tertiary,
        },
        color: theme.colors.onTertiary,
      },
      gradient: {
        vars: {
          "--avatar-background": `linear-gradient(110deg, ${theme.colors.gradientA} -5%, ${theme.colors.gradientB} 50%, ${theme.colors.gradientC} 105%)`,
        },
        color: theme.colors.onPrimary,
      },
      background: {
        vars: {
          "--avatar-background": theme.colors.background,
        },
        color: theme.colors.onBackground,
      },
      info: {
        vars: {
          "--avatar-background": theme.colors.info,
        },
        color: theme.colors.onInfo,
      },
      infoSurface: {
        vars: {
          "--avatar-background": theme.colors.infoSurface,
        },
        color: theme.colors.info,
      },
      success: {
        vars: {
          "--avatar-background": theme.colors.success,
        },
        color: theme.colors.onSuccess,
      },
      successSurface: {
        vars: {
          "--avatar-background": theme.colors.successSurface,
        },
        color: theme.colors.success,
      },
      error: {
        vars: {
          "--avatar-background": theme.colors.error,
        },
        color: theme.colors.onError,
      },
      errorSurface: {
        vars: {
          "--avatar-background": theme.colors.errorSurface,
        },
        color: theme.colors.error,
      },
      warning: {
        vars: {
          "--avatar-background": theme.colors.warning,
        },
        color: theme.colors.onWarning,
      },
      warningSurface: {
        vars: {
          "--avatar-background": theme.colors.warningSurface,
        },
        color: theme.colors.warning,
      },
      accent: {
        vars: {
          "--avatar-background": theme.colors.primary,
        },
        color: theme.colors.onPrimary,
      },
      accentSurface: {
        vars: {
          "--avatar-background": theme.colors.infoSurface,
        },
        color: theme.colors.info,
      },
      user: {
        // Does not do anything
      },
    },
    gradient: {
      true: {
        ":after": {
          content: "''",
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: `linear-gradient(110deg, transparent -5%, ${theme.colors.background} 105%)`,
          zIndex: 1,
          opacity: 0.5,
        },
      },
    },
    interactive: {
      true: {
        cursor: "pointer",
        outline: `2px solid transparent`,
        outlineOffset: "-1px",
        transition: transition("outline"),
        selectors: {
          "&:hover": {
            outlineColor: theme.colors.primary,
          },
        },
      },
    },
  },
  defaultVariants: {
    size: "m",
    variant: "primary",
  },
});

export const initialsStyle = recipe({
  base: {
    display: "inline",
    position: "relative",
    zIndex: 1,
  },
  variants: {
    size: {
      xxs: {
        fontSize: rem(10),
      },
      xs: {
        fontSize: rem(13),
      },
      s: {
        fontSize: rem(14),
      },
      m: {
        fontSize: rem(15),
      },
      l: {
        fontSize: theme.spacings.m,
      },
      menu: {
        ...responsive({
          base: {
            fontSize: rem(13),
          },
          s: {
            fontSize: theme.spacings.s,
          },
        }),
      },
      xl: {
        fontSize: rem(20),
      },
      xxl: {
        fontSize: rem(24),
      },
      xxxl: {
        fontSize: rem(32),
      },
    },
  },
});

export const imageStyle = style({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  pointerEvents: "none",
  borderRadius: "50%",
  zIndex: 1,
});
